import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { useIsComponentStillOnItsPage } from '@namespace/cms';
import { useShallowMemo } from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { UserContext } from '@namespace/user';
import { MODE } from '../../constants';
import {
  getLineField,
  sportsbookActions,
  useLineField,
  useSportsbookStore
} from '../../store';
import { useFetchEventsFilter } from '../useFetchEventsFilter';
import filterByOnlyParsedNumbers from './filterByNumbers';

const { FETCH_EVENTS, FETCH_EVENTS_BY_LIMIT, SET_IS_LINE_CACHED } =
  sportsbookActions;

export const useFetchEvents = ({
  lineKey,
  serviceId,
  filter = null,
  sportId,
  categoryIds: categoryIdsProp = [],
  tournamentsIds: tournamentsIdsProp = [],
  initialEventsLimit,
  moreEventsLimit,
  isTodayFilterApplied
}) => {
  const lineKeyRef = useRef(lineKey);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFilterEventsLoaded, setIsFilterEventsLoaded] = useState(false); // todo refactor (remove)
  const [user] = useContext(UserContext);
  const { marginGroupId } = user;
  const { language: lang, timezone } = useI18n();
  // todo refactor: move 'empty' into selector OR (better) remove categories and tournaments dependency at all! (investigate)
  const { categories, tournaments } = useSportsbookStore([
    'categories',
    'tournaments'
  ]);
  const isCategoriesEmpty = isEmpty(categories);
  const isTournamentsEmpty = isEmpty(tournaments);
  const lineOffset = useLineField(lineKey, 'offset');

  const filterValue = useFetchEventsFilter(filter);
  const categoryIds = useShallowMemo(categoryIdsProp);
  const tournamentsIds = useShallowMemo(tournamentsIdsProp);
  const checkIsComponentStillOnItsPage = useIsComponentStillOnItsPage();

  useEffect(() => {
    lineKeyRef.current = lineKey;

    return () => {
      lineKeyRef.current = null;
    };
  }, [lineKey]);

  const fetchEvents = useCallback(async () => {
    setIsEventsLoading(true);
    await FETCH_EVENTS({
      lang,
      sportId,
      serviceId,
      categoryIds,
      filterValue,
      marginGroupId,
      lineKey,
      tournamentsIds: filterByOnlyParsedNumbers(tournamentsIds),
      limit: initialEventsLimit,
      lineKeyRef
    });

    if (lineKeyRef.current !== lineKey) {
      return;
    }

    setIsEventsLoading(false);

    if (!isTodayFilterApplied) {
      setIsFilterEventsLoaded(true);
    }
  }, [
    lang,
    sportId,
    serviceId,
    categoryIds,
    filterValue,
    marginGroupId,
    lineKey,
    tournamentsIds,
    initialEventsLimit
    // isTodayFilterApplied @TODO it trigger extra useless load because filter and this state change in different renders
  ]);

  const loadMoreEvents = useCallback(async () => {
    try {
      setIsLoadingMore(true);
      await FETCH_EVENTS_BY_LIMIT({
        lang,
        sportId,
        serviceId,
        categoryIds,
        filterValue,
        lineKey,
        tournamentsIds,
        offset: lineOffset,
        limit: moreEventsLimit,
        lineKeyRef
      });
      if (lineKeyRef.current !== lineKey) {
        return;
      }

      setIsLoadingMore(false);
    } catch {
      setIsLoadingMore(false);
    }
  }, [
    lang,
    sportId,
    serviceId,
    categoryIds,
    filterValue,
    lineKey,
    tournamentsIds,
    lineOffset,
    moreEventsLimit
  ]);

  useEffect(() => {
    setIsFilterEventsLoaded(false);
  }, [filterValue]);

  useEffect(() => {
    // need to wait when categories and tournaments are loaded because only on
    // this stage we already have all data for correct requests without extra requests
    // to display relevant events for user without store overwrite
    if (
      sportId &&
      (serviceId === MODE.IN_PLAY ||
        (!isCategoriesEmpty && !isTournamentsEmpty))
    ) {
      if (getLineField(lineKey, 'isCached')) {
        SET_IS_LINE_CACHED(false, lineKey);
        setIsEventsLoading(false);
        return;
      }

      if (!checkIsComponentStillOnItsPage()) {
        return;
      }

      fetchEvents().catch(console.error);
    }
  }, [
    lineKey,
    timezone,
    sportId,
    isCategoriesEmpty,
    isTournamentsEmpty,
    serviceId,
    fetchEvents,
    checkIsComponentStillOnItsPage
  ]);

  return {
    isEventsLoading,
    isLoadingMore,
    loadMoreEvents,
    isFilterEventsLoaded
  };
};
